import { FC } from 'react';

import { getImageSize } from 'utils/clickme';
import { AiImageQueueType, BannerImageCropType, ImageComponent, UpscaleStatus } from 'utils/constants/banner';
import { Settings } from 'utils/constants/dynamicSettings';

import cropModalUpscaleButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/upscale/crop_modal_upscale_button_click';
import { Button, Card, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { HyperstarSparkleFilledSize16, ShareIosOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import { fetchUpscale } from 'components/common/BannerImg/CropImageModal/fetchUpscale';

import { setBannerImageComponentByType, setBannerImageUpscaleStatusByType } from 'store/models/createBanner';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerImg/CropImageModal/ImageContent/ImageInput/ImageInput.module.less';

interface ImageInputProps {
    cropType: BannerImageCropType;
    isSomeCropLoading: boolean;
    uploadNewImage: () => void;
}

const TrlKeys = {
    text: 'AdCreate.AdForm.CropImage.input.text',
    textUpscale: 'BannerImg.CropImageModal.input.textUpscaleMagritte',
    upscaleButton: 'BannerImg.CropImageModal.upscale',
    uploadButton: 'AdCreate.AdForm.CropImage.upload',
};

export const ImageInput: FC<ImageInputProps> = ({ cropType, isSomeCropLoading, uploadNewImage }) => {
    const originalImageId = useSelector((state) => state.createBanner.images.TARGET_DEFAULT.id);

    const trl = useTrls();
    const settings = useSettings();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();

    const isUpscaleEnabled = settings(Settings.IsUpscaleEnabled);

    const onUpscale = () => {
        cropModalUpscaleButtonClick({ buttonName: 'upscale', bannerImageCropType: cropType });
        dispatch([
            setBannerImageComponentByType({
                cropType,
                component: ImageComponent.Loading,
            }),
            setBannerImageUpscaleStatusByType({
                cropType,
                upscaleStatus: UpscaleStatus.None,
            }),
        ]);

        void dispatch(fetchUpscale(originalImageId as number, cropType, AiImageQueueType.Upscale));
    };

    return (
        <Card padding={12} borderWidth="default" stretched>
            <div className={styles.cropImageContentInput}>
                <Text typography={isMobile ? 'label-2-regular' : 'label-4-regular'}>
                    {isUpscaleEnabled ? trl(TrlKeys.textUpscale) : trl(TrlKeys.text, getImageSize(cropType))}
                </Text>
                <VSpacing default={28} />
                {isUpscaleEnabled && (
                    <>
                        <Button
                            mode="secondary"
                            style="neutral"
                            icon={<HyperstarSparkleFilledSize16 initialColor="negative" />}
                            onClick={onUpscale}
                            disabled={isSomeCropLoading}
                            stretched
                            data-qa={`upscale-button-${cropType}`}
                            spaceBetween={false}
                            size="small"
                        >
                            {trl(TrlKeys.upscaleButton)}
                        </Button>
                        <VSpacing default={12} />
                    </>
                )}
                <Button
                    mode="secondary"
                    style="neutral"
                    icon={<ShareIosOutlinedSize16 />}
                    onClick={uploadNewImage}
                    disabled={isSomeCropLoading}
                    stretched
                    spaceBetween={false}
                    size="small"
                >
                    {trl(TrlKeys.uploadButton)}
                </Button>
            </div>
        </Card>
    );
};
